import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BriefcaseIcon,
  DocumentTextIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ShareIcon,
  TrophyIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Loading, Modal } from '../components/common';
import ShareableCard from '../components/ShareableCard';
import ReferralDashboard from '../components/ReferralDashboard';
import { getJobs, getJobStats } from '../services/jobService';
import { getResumes } from '../services/resumeService';

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [resumeCount, setResumeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareVariant, setShareVariant] = useState('weekly');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [jobStatsRes, jobsRes, resumesRes] = await Promise.all([
        getJobStats(),
        getJobs({ limit: 5 }),
        getResumes()
      ]);

      if (jobStatsRes.success) {
        setStats(jobStatsRes.data);
      }

      if (jobsRes.success) {
        setRecentJobs(jobsRes.data.jobs || []);
      }

      if (resumesRes.success) {
        setResumeCount(resumesRes.data.resumes?.length || 0);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Jobs Saved',
      value: stats?.total || 0,
      icon: BriefcaseIcon,
      href: '/jobs',
      color: 'bg-blue-500'
    },
    {
      name: 'Resumes',
      value: resumeCount,
      icon: DocumentTextIcon,
      href: '/resumes',
      color: 'bg-green-500'
    },
    {
      name: 'Tailored',
      value: stats?.stats?.tailored || 0,
      icon: SparklesIcon,
      href: '/jobs?status=tailored',
      color: 'bg-purple-500'
    },
    {
      name: 'Applied',
      value: stats?.stats?.applied || 0,
      icon: ArrowTrendingUpIcon,
      href: '/jobs?status=applied',
      color: 'bg-orange-500'
    }
  ];

  const hasResume = resumeCount > 0;
  const hasJobs = stats?.total > 0;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.name?.split(' ')[0] || 'there'}!
        </h1>
        <p className="mt-1 text-gray-600">
          Here&apos;s an overview of your job search progress.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.name} to={stat.href}>
            <Card className="hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.name}</p>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Get started card */}
        <Card title="Get Started" subtitle="Complete these steps to start applying">
          <div className="space-y-4">
            <div className={`flex items-center gap-4 p-3 rounded-lg ${hasResume ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                hasResume ? 'bg-green-500 text-white' : 'bg-primary-100 text-primary-600'
              }`}>
                {hasResume ? <CheckCircleIcon className="h-5 w-5" /> : '1'}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${hasResume ? 'text-green-900' : 'text-gray-900'}`}>
                  {hasResume ? 'Resume uploaded' : 'Upload your resume'}
                </p>
                <p className={`text-sm ${hasResume ? 'text-green-700' : 'text-gray-600'}`}>
                  {hasResume ? `${resumeCount} resume${resumeCount > 1 ? 's' : ''} ready` : 'We\'ll parse it with AI'}
                </p>
              </div>
              <Link to="/resumes/new">
                <Button size="sm" variant={hasResume ? 'secondary' : 'primary'}>
                  {hasResume ? 'Add More' : 'Upload'}
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Install Chrome extension</p>
                <p className="text-sm text-gray-600">Capture jobs from LinkedIn</p>
              </div>
              <a
                href="https://chrome.google.com/webstore/detail/kwikly-ai-job-applications/hpmcmceiaoeladoigpihbhgedohacjhg"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="sm" variant="secondary">Install</Button>
              </a>
            </div>

            <div className={`flex items-center gap-4 p-3 rounded-lg ${hasJobs ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                hasJobs ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {hasJobs ? <CheckCircleIcon className="h-5 w-5" /> : '3'}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${hasJobs ? 'text-green-900' : 'text-gray-900'}`}>
                  {hasJobs ? 'Jobs captured!' : 'Capture your first job'}
                </p>
                <p className={`text-sm ${hasJobs ? 'text-green-700' : 'text-gray-600'}`}>
                  {hasJobs ? `${stats?.total} job${stats?.total > 1 ? 's' : ''} saved` : 'Browse LinkedIn and save jobs'}
                </p>
              </div>
              <Link to="/jobs">
                <Button size="sm" variant={hasJobs ? 'secondary' : 'primary'} disabled={!hasResume}>
                  {hasJobs ? 'View Jobs' : 'Get Started'}
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        {/* Recent activity */}
        <Card title="Recent Jobs" subtitle="Your latest saved jobs">
          {recentJobs.length > 0 ? (
            <div className="space-y-3">
              {recentJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{job.title}</p>
                      <p className="text-sm text-gray-600">{job.company}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      job.status === 'applied' ? 'bg-green-100 text-green-800' :
                      job.status === 'tailored' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                </Link>
              ))}
              <Link
                to="/jobs"
                className="block text-center text-sm text-primary-600 hover:text-primary-700 font-medium pt-2"
              >
                View all jobs →
              </Link>
            </div>
          ) : (
            <div className="text-center py-8">
              <BriefcaseIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No jobs captured yet</p>
              <p className="text-sm text-gray-500">
                Start by capturing jobs from LinkedIn or uploading your resume.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Share Your Progress */}
      {(stats?.total > 0 || stats?.stats?.tailored > 0) && (
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <TrophyIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">Share Your Progress</h3>
                <p className="mt-1 text-purple-700 text-sm">
                  Create a shareable card of your job search stats to inspire others!
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                setShareVariant('weekly');
                setShowShareModal(true);
              }}
            >
              <ShareIcon className="h-4 w-4 mr-2" />
              Share Stats
            </Button>
          </div>
        </Card>
      )}

      {/* Streak/Momentum Indicator */}
      {stats?.total >= 3 && (
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                <FireIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-orange-900 flex items-center gap-2">
                  You&apos;re on a roll!
                  <span className="text-2xl">🔥</span>
                </h3>
                <p className="mt-1 text-orange-700 text-sm">
                  {stats.total} jobs saved this week. Keep the momentum going!
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-orange-600">{stats.total}</p>
              <p className="text-sm text-orange-500">jobs this week</p>
            </div>
          </div>
        </Card>
      )}

      {/* Referral Program */}
      <ReferralDashboard user={user} />

      {/* Tips */}
      <Card className="bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-3 bg-primary-600 rounded-lg">
            <SparklesIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-primary-900">Pro Tip</h3>
            <p className="mt-1 text-primary-800">
              For best results, upload your master resume first. Our AI will extract all your experience
              and skills, making it easy to tailor for each job application.
            </p>
          </div>
        </div>
      </Card>

      {/* Share Modal */}
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Share Your Progress"
        size="lg"
      >
        <div className="space-y-4">
          {/* Variant selector */}
          <div className="flex justify-center gap-2 mb-4">
            <button
              onClick={() => setShareVariant('weekly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                shareVariant === 'weekly'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Weekly Stats
            </button>
            <button
              onClick={() => setShareVariant('ats-score')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                shareVariant === 'ats-score'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ATS Score
            </button>
          </div>

          <ShareableCard
            stats={{
              jobsApplied: stats?.total || 0,
              interviews: stats?.stats?.interviewing || 0,
              atsScore: 85, // Would come from user's best score
              timeSaved: Math.round((stats?.total || 0) * 1.5), // Estimate ~1.5 hours saved per job
              tailoredResumes: stats?.stats?.tailored || 0
            }}
            userName={user?.name || 'Job Seeker'}
            variant={shareVariant}
          />
        </div>
      </Modal>
    </div>
  );
};

export default DashboardPage;
